import { logMessage, LOG_LEVELS } from './logging.js';

// Import Firebase modules
import { do_logout, setupAuthentication } from './auth.js';
import config from '../config/index.js';
import { initVouchModal } from './vouchmodal.js';
import { getUser } from './data/update_operations.js'; // Adjust the import path as needed
import { setImpersonateEmail, getImpersonateEmail } from './impersonation.js';

function loadHeader() {
  return new Promise((resolve, reject) => {
    const insertHeader = () => {
      const placeholder =
        document.getElementById('header-new-placeholder') ||
        document.getElementById('header-placeholder');

      if (!placeholder) {
        resolve();
        return;
      }

      const file =
        placeholder.id === 'header-new-placeholder'
          ? 'header_new.html'
          : 'header.html';

      fetch(file)
        .then((response) => response.text())
        .then((data) => {
          placeholder.innerHTML = data;
          resolve();
        })
        .catch((error) => {
          console.error('Error loading header:', error);
          reject(error);
        });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', insertHeader);
    } else {
      insertHeader();
    }
  });
}

function loadFooter() {
  return new Promise((resolve, reject) => {
    const insertFooter = () => {
      const placeholder =
        document.getElementById('footer-old-placeholder') ||
        document.getElementById('footer-placeholder');

      if (!placeholder) {
        resolve();
        return;
      }

      const file =
        placeholder.id === 'footer-old-placeholder'
          ? 'footer_old.html'
          : 'footer.html';

      fetch(file)
        .then((response) => response.text())
        .then((data) => {
          placeholder.innerHTML = data;
          initializeNewsletterSignup();
          resolve();
        })
        .catch((error) => {
          console.error('Error loading footer:', error);
          reject(error);
        });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', insertFooter);
    } else {
      insertFooter();
    }
  });
}

// Load header, then set up the menu functionality
loadHeader().then(() => {
  try {
    updateAppNavigationVisibility();
    setActiveAppNavLinks();
    setupMenu();
    if (!window.location.pathname.includes('signup.html')) {
      setupAuthentication(
        (user, token) => handleAuthenticatedUser(user, token, true),
        handleUnauthenticatedUser
      );
    }
    setupImpersonationControl();
    logMessage(LOG_LEVELS.INFO, 'Menu setup complete.');
  } catch (error) {
    logMessage(LOG_LEVELS.ERROR, 'Error setting up menu', {
      error: error.message,
    });
  }
});

loadFooter();

function updateAppNavigationVisibility() {
  try {
    const isAppPage =
      document.body && document.body.classList.contains('app-nav');

    document.querySelectorAll('[data-app-nav]').forEach((element) => {
      if (!(element instanceof HTMLElement)) {
        return;
      }

      if (isAppPage) {
        element.style.removeProperty('display');
        element.removeAttribute('aria-hidden');
      } else {
        element.style.display = 'none';
        element.setAttribute('aria-hidden', 'true');
      }
    });
  } catch (error) {
    console.warn('updateAppNavigationVisibility error:', error);
  }
}

function setActiveAppNavLinks() {
  try {
    const path = (window.location && window.location.pathname) || '';

    // Clear any existing aria-current
    document.querySelectorAll('.app-links__item[aria-current], .tabbar__item[aria-current]')
      .forEach((el) => el.removeAttribute('aria-current'));

    const isDiscover = /(^|\/)discover(\.html)?$/.test(path) || path === '/' || /index\.html$/.test(path);
    const isDashboard = /(^|\/)dashboard(\.html)?$/.test(path);
    const isIntegrations = /(^|\/)integrations(\.html)?$/.test(path);

    const selectorsToMark = [];
    if (isDiscover) {
      selectorsToMark.push('a.app-links__item[href$="/discover.html"]', 'a.app-links__item[href$="/discover"]');
      selectorsToMark.push('a.tabbar__item[href$="/discover.html"]', 'a.tabbar__item[href$="/discover"]');
    } else if (isDashboard) {
      selectorsToMark.push('a.app-links__item[href$="/dashboard.html"]', 'a.app-links__item[href$="/dashboard"]');
      selectorsToMark.push('a.tabbar__item[href$="/dashboard.html"]', 'a.tabbar__item[href$="/dashboard"]');
    } else if (isIntegrations) {
      selectorsToMark.push('a.app-links__item[href$="/integrations.html"]', 'a.app-links__item[href$="/integrations"]');
      selectorsToMark.push('a.tabbar__item[href$="/integrations.html"]', 'a.tabbar__item[href$="/integrations"]');
    }

    selectorsToMark.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => el.setAttribute('aria-current', 'page'));
    });
  } catch (e) {
    console.warn('setActiveAppNavLinks error:', e);
  }
}

function setupImpersonationControl() {
  const input = document.getElementById('impersonate-email-input');
  const button = document.getElementById('set-impersonate-btn');
  if (!input || !button) return;

  const existing = getImpersonateEmail();
  if (existing) {
    input.value = existing;
  }

  button.addEventListener('click', () => {
    const email = input.value.trim();
    setImpersonateEmail(email);
    alert(
      email
        ? `Impersonation email set to ${email}`
        : 'Impersonation cleared'
    );
  });
}

function setupMenu() {
  const menu = document.getElementById('menuButton');
  if (!menu) return;
  menu.addEventListener('click', function () {
    toggleMenu();
  });
}

function toggleMenu() {
  try {
    // grab both navbars, filter out any that don't exist
    const navbars = [
      document.querySelector('.navbar'),
      document.querySelector('.navbar-new'),
    ].filter((el) => el instanceof HTMLElement);

    if (navbars.length === 0) {
      console.warn('toggleMenu: no menu elements found.');
      return;
    }

    navbars.forEach((nav) => {
      nav.classList.toggle('open');
    });
  } catch (err) {
    console.error('toggleMenu encountered an error:', err);
  }
}

function hideButton(btn) {
  if (!btn) return;
  btn.style.display = 'none';
  if (btn.parentElement && btn.parentElement.tagName !== 'NAV') {
    btn.parentElement.style.display = 'none';
  }
}

function showButton(btn) {
  if (!btn) return;
  btn.style.display = 'block';
  if (btn.parentElement && btn.parentElement.tagName !== 'NAV') {
    btn.parentElement.style.display = 'block';
  }
}

async function handleAuthenticatedUser(user, token, registrationConfirmed = false) {
  updateAppNavigationVisibility();
  const loginButton = document.getElementById('login-btn');
  const signupButton = document.getElementById('signup-btn');
  const logoutButton = document.getElementById('logout-btn');
  const accountLink = document.getElementById('account-link');
  const inboxLink = document.getElementById('inbox-link');
  const publicProfileLink = document.getElementById('public-profile-link');
  const featuresLink = document.getElementById('features-link');
  const pricingLink = document.getElementById('pricing-link');

  // User is signed in
  hideButton(loginButton);
  hideButton(signupButton);
  if (logoutButton) {
    showButton(logoutButton);
    logoutButton.addEventListener('click', logoutUser);
  }
  if (accountLink) accountLink.style.display = 'block';
  if (inboxLink) inboxLink.style.display = 'block';
  if (featuresLink) featuresLink.style.display = 'none';
  if (pricingLink) pricingLink.style.display = 'none';

  if (!registrationConfirmed) {
    return;
  }

  const serverside_user = await getUserWrapper(token);

  if (!serverside_user) {
    return;
  }

  if (publicProfileLink) {
    publicProfileLink.style.display = 'block';
    publicProfileLink.href = `/profile.html?username=${serverside_user.username}`;
  }

  const vouchModalEl = document.getElementById('vouch-modal');
  if (vouchModalEl) {
    const vouchModal = initVouchModal();
    const vouchButton = document.getElementById('initiateVouchButton');

    if (vouchButton) {
      vouchButton.style.display = 'flex';
      vouchButton.addEventListener('click', () => vouchModal.open());
    }
  }

  checkUnreadCount(token);
}

async function checkUnreadCount(token) {
  try {
    const res = await fetch(`${config.detectionUrl}/unread-count`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      console.error('Error fetching unread count');
      return;
    }

    const data = await res.json();
    const unreadCount = data.unread_count || 0;

    // Show or hide the dot
    const dot = document.getElementById('inbox-unread-dot');
    if (dot) {
      dot.style.display = unreadCount > 0 ? 'block' : 'none';
    }
  } catch (err) {
    console.error('Error checking unread count:', err);
  }
}

async function getUserWrapper(token) {
  try {
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    return await getUser(headers);
  } catch (err) {
    console.error('Error getting user:', err);
  }
}

function handleUnauthenticatedUser() {
  updateAppNavigationVisibility();
  const loginButton = document.getElementById('login-btn');
  const signupButton = document.getElementById('signup-btn');
  const logoutButton = document.getElementById('logout-btn');
  const accountLink = document.getElementById('account-link');
  const homeLink = document.getElementById('home-link');
  const integrationsLink = document.getElementById('integrations-link');
  const publicProfileLink = document.getElementById('public-profile-link');
  const featuresLink = document.getElementById('features-link');
  const pricingLink = document.getElementById('pricing-link');

  const path = window.location.pathname;
  const hasLoggedInBefore = localStorage.getItem('hasLoggedInBefore') === 'true';

  if (hasLoggedInBefore) {
    if (path.includes('login.html')) {
      hideButton(loginButton);
    } else {
      showButton(loginButton);
    }
    hideButton(signupButton);
  } else {
    if (path.includes('signup.html')) {
      showButton(loginButton);
      hideButton(signupButton);
    } else {
      hideButton(loginButton);
      showButton(signupButton);
    }
  }

  // User is signed out
  if (logoutButton) {
    hideButton(logoutButton);
    logoutButton.removeEventListener('click', logoutUser);
  }
  if (accountLink) accountLink.style.display = 'none';
  if (homeLink) homeLink.style.display = 'none';
  if (integrationsLink) integrationsLink.style.display = 'none';
  if (featuresLink) featuresLink.style.display = 'block';
  if (pricingLink) pricingLink.style.display = 'block';
  if (publicProfileLink) publicProfileLink.style.display = 'none';
}

function logoutUser(event) {
  event.preventDefault(); // Prevent default link behavior
  do_logout();
}

function initializeNewsletterSignup() {
  const form = document.querySelector('.newsletter-form');
  if (!form) return;

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const emailInput = form.querySelector('input[type="email"]');
    if (!emailInput) return;

    const email = emailInput.value.trim();
    if (!email) return;

    try {
      const res = await fetch(`${config.detectionUrl}/newsletter-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        emailInput.value = '';
        alert('Thank you for subscribing!');
      } else {
        alert('Subscription failed. Please try again.');
      }
    } catch (err) {
      console.error('Newsletter signup error:', err);
      alert('Subscription failed. Please try again.');
    }
  });
}

export { handleAuthenticatedUser, handleUnauthenticatedUser };
